import React, { useMemo, useState } from 'react';
import { DataBoxLegendProps } from 'src/components/molecules/DataBox';
import { KeyMetricGraphBox } from 'src/components/organisms/KeyMetricGraphBox';
import { useAggregationLevelContext } from 'src/features/MM/contexts/AggregationLevelContext';
import { useGetDateAxisFormatter } from 'src/features/MM/hooks/useGetDateAxisFormatter';
import { getAggregateLevelData } from 'src/features/MM/utils/getAggregateLevelData';
import { useChartEffectTickFormatter } from 'src/hooks/useEffectFormatter';
import { combineTwoSimplifiedTimeframeData } from 'src/utils/combine';
import { useMMDataContext } from '../../contexts/MMDataContext';
import { TimeseriesDataTableModal } from '../molecules/TimeseriesDataTableModal';
import {
  observedMetricDef,
  predictedMetricDef,
} from 'src/utils/metricDefinitions';
import { useKpiMetricLabels } from 'src/hooks/useKpiMetricLabels';

type ProcessedDataPoint = { utcEpoch: number } & Record<string, number | null>;

export const SalesAndPredictedSalesBox: React.FC = () => {
  const { sales, predictedSales } = useMMDataContext();
  const { aggregateLevel } = useAggregationLevelContext();
  const { metricLabel, predictedLabel } = useKpiMetricLabels();

  const [modalIsOpen, setModalIsOpen] = useState(false);

  const yAxisFormatter = useChartEffectTickFormatter();

  const xAxisFormatter = useGetDateAxisFormatter();

  const legendProps: DataBoxLegendProps = useMemo(
    () => ({
      placement: 'top',
      baseLegendKeys: [metricLabel],
      lineKeys: [predictedLabel],
      keyColorMap: {
        [predictedLabel]: '#EAA16F',
      },
    }),
    [metricLabel, predictedLabel],
  );

  const processedData = useMemo(() => {
    const salesData = getAggregateLevelData(sales, aggregateLevel);
    const predictedSalesData = getAggregateLevelData(
      predictedSales,
      aggregateLevel,
    );

    const combinedData: Array<ProcessedDataPoint> =
      combineTwoSimplifiedTimeframeData(
        salesData,
        metricLabel,
        predictedSalesData,
        predictedLabel,
      );

    return combinedData;
  }, [sales, predictedSales, aggregateLevel, metricLabel, predictedLabel]);

  return (
    <KeyMetricGraphBox
      title={metricLabel}
      legendProps={legendProps}
      data={processedData}
      xAxisFormatter={xAxisFormatter}
      yAxisFormatter={yAxisFormatter}
      isError={sales.isError}
      isLoading={sales.isLoading}
      infoText={`${observedMetricDef(metricLabel)}\n\n${predictedMetricDef(metricLabel)}`}
      exportEnabled
      onTableModalOpen={setModalIsOpen}
      tableModal={
        <TimeseriesDataTableModal
          isOpen={modalIsOpen}
          setIsOpen={setModalIsOpen}
          yFormatter={yAxisFormatter}
          data={processedData}
          headerName={metricLabel}
        />
      }
    />
  );
};
